/**
 * 鉴权 API hooks
 *
 * - useClientLoginMethods: GET /app/auth/methods
 * - useSendSmsCode: POST /app/auth/sms/code
 * - useSmsLogin:    POST /app/auth/login (method=sms)
 * - useWechatAuthorizeUrl + useWechatMpQrCode + useWechatMpQrStatus + useWechatTicketLogin:
 *   桌面 Web 微信授权 / 移动端公众号扫码登录
 *
 * 登录成功后统一调 client/legal/profile/me 拿聚合资料并写入 useAuthStore.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { http } from '@/lib/http'
import { useAuthStore, type Role, type UserSex } from '@/stores/auth'
import type {
  CaptchaVo,
  ClientLoginOptionsVo,
  ClientLoginVo,
  ClientSmsCodeBo,
  PasswordLoginBody,
  SmsLoginBody,
  WechatMpQrCodeVo,
  WechatMpQrLoginStatusVo,
  WechatWebAuthorizeUrlVo,
  WechatLoginBody,
} from '@/types/api'
import { fetchClientUserInfo } from '@/features/user/api'
import {
  clearInvitationCode,
  invitationLoginFields,
  persistInvitationForRedirect,
} from './invitation'

const CLIENT_ID = import.meta.env.VITE_AUTH_CLIENT_ID as string
const TENANT_ID = (import.meta.env.VITE_AUTH_TENANT_ID as string | undefined)?.trim()
const DEFAULT_EXPIRE_IN_SECONDS = 604800

/**
 * 登录态落地: 拿到 ClientLoginVo 后立即拉 C 端用户信息, 一并写入 store, 返回前端 roles.
 */
async function applyLoginAndFetchInfo(loginVo: ClientLoginVo) {
  const { setAuth, setRoles, setUserInfo } = useAuthStore.getState()
  if (!loginVo.accessToken) {
    throw new Error('登录响应缺少 accessToken')
  }
  const accessToken = normalizeAccessToken(loginVo.accessToken)
  // 先把 token 写入 store, 这样 fetchClientUserInfo 才能带上 Authorization 头
  setAuth(accessToken, normalizeExpireIn(loginVo.expireIn))
  // 后端已完成登录和首次注册，资料拉取失败也不能保留旧邀请码。
  clearInvitationCode()

  const info = await fetchClientUserInfo()
  const roles = normalizeRoles(info.roles ?? [])
  setRoles(roles)
  setUserInfo({
    userId: String(info.userId ?? ''),
    username: info.nickName ?? '',
    nickname: info.nickName ?? '',
    avatar: info.avatar,
    phonenumber: info.phonenumber,
    email: info.email,
    sex: normalizeUserSex(info.sex),
  })
  return { roles }
}

function normalizeExpireIn(expireIn: number | string | undefined): number {
  const seconds = typeof expireIn === 'number' ? expireIn : Number(expireIn)
  return Number.isFinite(seconds) && seconds > 0 ? seconds : DEFAULT_EXPIRE_IN_SECONDS
}

function normalizeAccessToken(token: string): string {
  const cleaned = token
    .trim()
    .replace(/^Bearer\s+/i, '')
    .trim()
  if (!cleaned || /[\r\n]/.test(cleaned)) {
    throw new Error('登录响应 token 格式异常')
  }
  return cleaned
}

function normalizeRoles(roles: ReadonlyArray<string>): Role[] {
  return Array.from(new Set(roles.filter(Boolean)))
}

function normalizeUserSex(raw: string | undefined): UserSex | undefined {
  return raw === '0' || raw === '1' || raw === '2' ? raw : undefined
}

function withTenant<T extends object>(payload: T): T & { tenantId?: string } {
  return TENANT_ID ? { ...payload, tenantId: TENANT_ID } : payload
}

const clientLoginMethodsQueryKey = ['auth', 'methods', CLIENT_ID, TENANT_ID ?? ''] as const

/** 登录弹窗每次打开时发现后端当前启用的登录方式。 */
export function useClientLoginMethods(enabled = true) {
  return useQuery({
    queryKey: clientLoginMethodsQueryKey,
    queryFn: async () => {
      if (!CLIENT_ID) throw new Error('未配置 VITE_AUTH_CLIENT_ID')
      return http.get<ClientLoginOptionsVo>('/app/auth/methods', {
        params: withTenant({ clientId: CLIENT_ID }),
      })
    },
    enabled,
    staleTime: 0,
    retry: 1,
  })
}

function useRefreshLoginMethodsOnUnavailable() {
  const queryClient = useQueryClient()
  return (error: Error) => {
    if (error.message.includes('当前登录方式不可用')) {
      void queryClient.invalidateQueries({ queryKey: clientLoginMethodsQueryKey })
    }
  }
}

/* ---------- 短信验证码 ---------- */

export function useSendSmsCode() {
  const refreshMethods = useRefreshLoginMethodsOnUnavailable()
  return useMutation({
    mutationKey: ['auth', 'send-sms'],
    mutationFn: async (phonenumber: string) => {
      const body = withTenant<ClientSmsCodeBo>({
        clientId: CLIENT_ID,
        phonenumber,
      })
      return http.post<void>('/app/auth/sms/code', body, {
        headers: { isEncrypt: 'true' },
      })
    },
    onError: refreshMethods,
  })
}

interface SmsLoginInput {
  phonenumber: string
  smsCode: string
}

export function useSmsLogin() {
  const refreshMethods = useRefreshLoginMethodsOnUnavailable()
  return useMutation({
    mutationKey: ['auth', 'sms'],
    mutationFn: async ({ phonenumber, smsCode }: SmsLoginInput) => {
      const body = withTenant<SmsLoginBody>({
        clientId: CLIENT_ID,
        method: 'sms',
        ...invitationLoginFields(),
        payload: { phonenumber, smsCode },
      })
      const loginVo = await http.post<ClientLoginVo>('/app/auth/login', body, {
        headers: { isEncrypt: 'true' },
      })
      return applyLoginAndFetchInfo(loginVo)
    },
    onError: refreshMethods,
  })
}

/* ---------- 用户名密码登录 ---------- */

interface PasswordLoginInput {
  username: string
  password: string
  code?: string
  uuid?: string
}

export function useCaptchaCode() {
  return useMutation({
    mutationKey: ['auth', 'captcha'],
    mutationFn: async () => http.get<CaptchaVo>('/auth/code'),
  })
}

export function usePasswordLogin() {
  const refreshMethods = useRefreshLoginMethodsOnUnavailable()
  return useMutation({
    mutationKey: ['auth', 'password'],
    mutationFn: async ({ username, password, code, uuid }: PasswordLoginInput) => {
      const body = withTenant<PasswordLoginBody>({
        clientId: CLIENT_ID,
        method: 'password',
        payload: {
          username,
          password,
          ...(code ? { code } : {}),
          ...(uuid ? { uuid } : {}),
        },
      })
      const loginVo = await http.post<ClientLoginVo>('/app/auth/login', body, {
        headers: { isEncrypt: 'true' },
      })
      return applyLoginAndFetchInfo(loginVo)
    },
    onError: refreshMethods,
  })
}

/* ---------- 微信登录 ---------- */

interface WechatAuthorizeUrlInput {
  /** 前端回跳 URL, 必须是后端白名单内的完整 http/https 地址 */
  redirect: string
  provider?: 'wechat_open' | 'wechat_mp'
  scene?: string
}

export function useWechatAuthorizeUrl() {
  return useMutation({
    mutationKey: ['auth', 'wechat', 'authorize-url'],
    mutationFn: async ({ redirect, provider = 'wechat_open', scene }: WechatAuthorizeUrlInput) => {
      persistInvitationForRedirect()
      return http.get<WechatWebAuthorizeUrlVo>('/app/auth/wechat/url', {
        params: withTenant({ clientId: CLIENT_ID, provider, redirect, scene }),
      })
    },
  })
}

interface WechatMpQrCodeInput {
  /** 前端回跳 URL, 必须是后端白名单内的完整 http/https 地址 */
  redirect: string
}

export function useWechatMpQrCode() {
  return useMutation({
    mutationKey: ['auth', 'wechat', 'mp-qr', 'code'],
    mutationFn: async ({ redirect }: WechatMpQrCodeInput) =>
      http.get<WechatMpQrCodeVo>('/app/auth/wechat/qr/code', {
        params: withTenant({ clientId: CLIENT_ID, redirect }),
      }),
  })
}

export function useWechatMpQrStatus() {
  return useMutation({
    mutationKey: ['auth', 'wechat', 'mp-qr', 'status'],
    mutationFn: async (sceneId: string) =>
      http.get<WechatMpQrLoginStatusVo>('/app/auth/wechat/qr/status', {
        params: { sceneId },
      }),
  })
}

export function useWechatTicketLogin() {
  const refreshMethods = useRefreshLoginMethodsOnUnavailable()
  return useMutation({
    mutationKey: ['auth', 'wechat', 'ticket-login'],
    mutationFn: async (ticket: string) => {
      const body = withTenant<WechatLoginBody>({
        clientId: CLIENT_ID,
        method: 'wechat',
        ...invitationLoginFields(),
        payload: { ticket },
      })
      const loginVo = await http.post<ClientLoginVo>('/app/auth/login', body, {
        headers: { isEncrypt: 'true' },
      })
      return applyLoginAndFetchInfo(loginVo)
    },
    onError: refreshMethods,
  })
}
